import React, { ReactElement } from 'react';

import { EvFlexRow, EvTableColumn, MultilineTextCellFormatter } from '@evinced-private/ui-common';

import {
	IMobileAllIssue,
	IMobileUniqueIssue
} from 'src/apps/dashboard/types/MobileDashboardServiceTypes';
import { NumberedBadge } from 'src/common/components/numbered-badge/NumberedBadge';
import { SeverityCellFormatter } from 'src/common/components/severity-cell-formatter/SeverityCellFormatter';
import { ShowIssueDetailsCellFormatter } from 'src/common/components/show-details-cell-formatter/ShowIssueDetailsCellFormatter';
import { formatShortMonthDatetime } from 'src/common/helpers/DateFormatHelper';

import { AllIssuesDetailsBlock } from '../components/AllIssuesDetailsBlock';
import { UniqueIssuesDetailsBlock } from '../components/UniqueIssuesDetailsBlock';
import { IAllIssue, IUniqueIssue } from '../types/IssuesTypes';

import { MOBILE_ISSUES_COLUMN_LABELS } from './IssuesTableColumns.labels';

const ALL_ISSUES_COLUMN_WIDTHS = {
	DEFAULT: '164.4px',
	PAGE_TITLE: '117px',
	PAGE_URL: '220px',
	ACTIONS: '56px'
} as const;

const UNIQUE_ISSUES_COLUMN_WIDTHS = {
	DEFAULT: '180px',
	SEVERITY: '120px',
	PAGE_URL: '220px',
	ACTIONS: '50px'
} as const;

export const ALL_ISSUES_COLUMNS: EvTableColumn[] = [
	{
		dataField: 'issueType',
		text: 'Issue Type',
		sort: true,
		style: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		headerStyle: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		formatter: (text: string): ReactElement => (
			<MultilineTextCellFormatter items={[text]} itemType="issueType" showTooltip />
		)
	},
	{
		dataField: 'severity',
		text: 'Severity',
		sort: true,
		style: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			textAlign: 'center'
		},
		headerStyle: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			textAlign: 'center'
		},
		formatter: (severity: string): ReactElement => (
			<MultilineTextCellFormatter items={[severity]} itemType="severity" showTooltip />
		)
	},
	{
		dataField: 'pageTitle',
		text: 'Page Title',
		sort: true,
		style: {
			width: ALL_ISSUES_COLUMN_WIDTHS.PAGE_TITLE,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.PAGE_TITLE
		},
		headerStyle: {
			width: ALL_ISSUES_COLUMN_WIDTHS.PAGE_TITLE,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.PAGE_TITLE
		},
		formatter: (text: string): ReactElement => (
			<MultilineTextCellFormatter items={[text]} itemType="pageTitle" showTooltip />
		)
	},
	{
		dataField: 'pageUrl',
		text: 'Page URL',
		sort: true,
		style: {
			width: ALL_ISSUES_COLUMN_WIDTHS.PAGE_URL,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.PAGE_URL
		},
		headerStyle: {
			width: ALL_ISSUES_COLUMN_WIDTHS.PAGE_URL,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.PAGE_URL
		},
		formatter: (url: string): ReactElement => (
			<a
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				style={{
					display: 'block',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap'
				}}
			>
				{url}
			</a>
		)
	},
	{
		dataField: 'foundBy',
		text: 'Found By',
		sort: true,
		style: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		headerStyle: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT
		}
	},
	{
		dataField: 'sessionOrTest',
		text: 'Session / Test',
		sort: true,
		style: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		headerStyle: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		formatter: (text: string): ReactElement => (
			<MultilineTextCellFormatter items={[text]} itemType="sessionOrTest" showTooltip />
		)
	},
	{
		dataField: 'user',
		text: 'User',
		sort: true,
		style: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		headerStyle: {
			width: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.DEFAULT
		}
	},
	{
		dataField: 'actions',
		text: '',
		style: {
			width: ALL_ISSUES_COLUMN_WIDTHS.ACTIONS,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.ACTIONS
		},
		headerStyle: {
			width: ALL_ISSUES_COLUMN_WIDTHS.ACTIONS,
			maxWidth: ALL_ISSUES_COLUMN_WIDTHS.ACTIONS
		},
		formatter: (_cell: unknown, row: IAllIssue): ReactElement => {
			const renderDetailsBlock = (setError: (msg: string) => void): ReactElement => (
				<AllIssuesDetailsBlock
					runId={row.runId}
					issueId={row.id}
					productName={row.productName}
					setError={setError}
				/>
			);

			return (
				<ShowIssueDetailsCellFormatter
					renderDetailsBlock={renderDetailsBlock}
					issueType={row.issueType}
					infoTitle={`${row.sessionOrTest} - ${row.foundBy}`}
				/>
			);
		}
	}
];

const MOBILE_COLUMN_WIDTHS = {
	DEFAULT: '160px',
	SEVERITY: '120px',
	ELEMENT: '220px',
	ACTIONS: '56px'
} as const;

const renderTextCell = (text: string, itemType: string): ReactElement => (
	<MultilineTextCellFormatter items={[text]} itemType={itemType} showTooltip />
);

export const MOBILE_ALL_ISSUES_COLUMNS: EvTableColumn[] = [
	{
		dataField: 'issueType',
		text: MOBILE_ISSUES_COLUMN_LABELS.ISSUE_TYPE,
		sort: true,
		style: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		formatter: (text: string): ReactElement => renderTextCell(text, 'issueType')
	},
	{
		dataField: 'severity',
		text: MOBILE_ISSUES_COLUMN_LABELS.SEVERITY,
		sort: true,
		style: {
			width: MOBILE_COLUMN_WIDTHS.SEVERITY,
			maxWidth: MOBILE_COLUMN_WIDTHS.SEVERITY
		},
		headerStyle: {
			width: MOBILE_COLUMN_WIDTHS.SEVERITY,
			maxWidth: MOBILE_COLUMN_WIDTHS.SEVERITY
		},
		formatter: (severity: string): ReactElement => <SeverityCellFormatter severity={severity} />
	},
	{
		dataField: 'appId',
		text: MOBILE_ISSUES_COLUMN_LABELS.APP_ID,
		sort: true,
		style: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT }
	},
	{
		dataField: 'elementDescription',
		text: MOBILE_ISSUES_COLUMN_LABELS.ELEMENT_DESCRIPTION,
		sort: true,
		style: { width: MOBILE_COLUMN_WIDTHS.ELEMENT, maxWidth: MOBILE_COLUMN_WIDTHS.ELEMENT },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.ELEMENT, maxWidth: MOBILE_COLUMN_WIDTHS.ELEMENT },
		formatter: (text: string, row: IMobileAllIssue): ReactElement => (
			<EvFlexRow align="CENTER" wrap="NOWRAP">
				{row.elementIndex && <NumberedBadge number={row.elementIndex} />}
				{renderTextCell(text, 'elementDescription')}
			</EvFlexRow>
		)
	},
	{
		dataField: 'foundBy',
		text: MOBILE_ISSUES_COLUMN_LABELS.FOUND_BY,
		sort: true,
		style: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		formatter: (text: string): ReactElement => renderTextCell(text, 'foundBy')
	},
	{
		dataField: 'sessionOrTest',
		text: MOBILE_ISSUES_COLUMN_LABELS.SESSION_OR_TEST,
		sort: true,
		style: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		formatter: (text: string): ReactElement => renderTextCell(text, 'sessionOrTest')
	},
	{
		dataField: 'user',
		text: MOBILE_ISSUES_COLUMN_LABELS.USER,
		sort: true,
		style: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT }
	},
	{
		dataField: 'actions',
		text: '',
		style: { width: MOBILE_COLUMN_WIDTHS.ACTIONS, maxWidth: MOBILE_COLUMN_WIDTHS.ACTIONS },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.ACTIONS, maxWidth: MOBILE_COLUMN_WIDTHS.ACTIONS },
		formatter: (_cell: unknown, row: IMobileAllIssue): ReactElement => {
			const renderDetailsBlock = (setError: (msg: string) => void): ReactElement => (
				<AllIssuesDetailsBlock
					runId={row.runId}
					issueId={row.id}
					productName={row.productName}
					setError={setError}
				/>
			);
			return (
				<ShowIssueDetailsCellFormatter
					renderDetailsBlock={renderDetailsBlock}
					infoTitle={`${row.sessionOrTest} - ${row.foundBy}`}
				/>
			);
		}
	}
];

export const MOBILE_UNIQUE_ISSUES_COLUMNS: EvTableColumn[] = [
	{
		dataField: 'issueType',
		text: MOBILE_ISSUES_COLUMN_LABELS.ISSUE_TYPE,
		sort: true,
		style: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		formatter: (text: string): ReactElement => renderTextCell(text, 'issueType')
	},
	{
		dataField: 'severity',
		text: MOBILE_ISSUES_COLUMN_LABELS.SEVERITY,
		sort: true,
		style: {
			width: MOBILE_COLUMN_WIDTHS.SEVERITY,
			maxWidth: MOBILE_COLUMN_WIDTHS.SEVERITY
		},
		headerStyle: {
			width: MOBILE_COLUMN_WIDTHS.SEVERITY,
			maxWidth: MOBILE_COLUMN_WIDTHS.SEVERITY
		},
		formatter: (severity: string): ReactElement => <SeverityCellFormatter severity={severity} />
	},
	{
		dataField: 'appId',
		text: MOBILE_ISSUES_COLUMN_LABELS.APP_ID,
		sort: true,
		style: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.DEFAULT, maxWidth: MOBILE_COLUMN_WIDTHS.DEFAULT }
	},
	{
		dataField: 'elementDescription',
		text: MOBILE_ISSUES_COLUMN_LABELS.ELEMENT_DESCRIPTION,
		sort: true,
		style: { width: MOBILE_COLUMN_WIDTHS.ELEMENT, maxWidth: MOBILE_COLUMN_WIDTHS.ELEMENT },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.ELEMENT, maxWidth: MOBILE_COLUMN_WIDTHS.ELEMENT },
		formatter: (text: string, row: IMobileUniqueIssue): ReactElement => (
			<EvFlexRow align="CENTER" wrap="NOWRAP">
				{row.elementIndex && <NumberedBadge number={row.elementIndex} />}
				{renderTextCell(text, 'elementDescription')}
			</EvFlexRow>
		)
	},
	{
		dataField: 'firstSeen',
		text: MOBILE_ISSUES_COLUMN_LABELS.FIRST_SEEN,
		sort: true,
		style: { width: '180px' },
		formatter: (date: string) => (date ? formatShortMonthDatetime(date) : '')
	},
	{
		dataField: 'lastSeen',
		text: MOBILE_ISSUES_COLUMN_LABELS.LAST_SEEN,
		sort: true,
		style: { width: '180px' },
		formatter: (date: string) => (date ? formatShortMonthDatetime(date) : '')
	},
	{
		dataField: 'instanceCount',
		text: MOBILE_ISSUES_COLUMN_LABELS.INSTANCE_COUNT,
		sort: true,
		style: { width: '120px' },
		headerStyle: { width: '120px' }
	},
	{
		dataField: 'actions',
		text: '',
		style: { width: MOBILE_COLUMN_WIDTHS.ACTIONS, maxWidth: MOBILE_COLUMN_WIDTHS.ACTIONS },
		headerStyle: { width: MOBILE_COLUMN_WIDTHS.ACTIONS, maxWidth: MOBILE_COLUMN_WIDTHS.ACTIONS },
		formatter: (_cell: unknown, row: IMobileUniqueIssue): ReactElement => {
			const renderDetailsBlock = (setError: (msg: string) => void): ReactElement => (
				<UniqueIssuesDetailsBlock
					runId={row.runId}
					issueId={row.issueId}
					productName={row.productName}
					setError={setError}
				/>
			);
			return (
				<ShowIssueDetailsCellFormatter
					renderDetailsBlock={renderDetailsBlock}
					infoTitle={MOBILE_ISSUES_COLUMN_LABELS.ISSUE_DETAILS}
				/>
			);
		}
	}
];

export const UNIQUE_ISSUES_COLUMNS: EvTableColumn[] = [
	{
		dataField: 'issueType',
		text: 'Issue Type',
		sort: true,
		style: {
			width: UNIQUE_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: UNIQUE_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		headerStyle: {
			width: UNIQUE_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: UNIQUE_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		formatter: (text: string) => (
			<MultilineTextCellFormatter items={[text]} itemType="issueType" showTooltip />
		)
	},
	{
		dataField: 'severity',
		text: 'Severity',
		sort: true,
		style: {
			width: UNIQUE_ISSUES_COLUMN_WIDTHS.SEVERITY,
			maxWidth: UNIQUE_ISSUES_COLUMN_WIDTHS.SEVERITY,
			textAlign: 'center'
		},
		headerStyle: {
			width: UNIQUE_ISSUES_COLUMN_WIDTHS.SEVERITY,
			maxWidth: UNIQUE_ISSUES_COLUMN_WIDTHS.SEVERITY,
			textAlign: 'center'
		},
		formatter: (severity: string): ReactElement => (
			<MultilineTextCellFormatter items={[severity]} itemType="severity" showTooltip />
		)
	},
	{
		dataField: 'pageTitle',
		text: 'Page Title',
		sort: true,
		style: {
			width: UNIQUE_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: UNIQUE_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		headerStyle: {
			width: UNIQUE_ISSUES_COLUMN_WIDTHS.DEFAULT,
			maxWidth: UNIQUE_ISSUES_COLUMN_WIDTHS.DEFAULT
		},
		formatter: (text: string) => (
			<MultilineTextCellFormatter items={[text]} itemType="pageTitle" showTooltip />
		)
	},
	{
		dataField: 'pageUrl',
		text: 'Page URL',
		sort: true,
		style: {
			width: UNIQUE_ISSUES_COLUMN_WIDTHS.PAGE_URL,
			maxWidth: UNIQUE_ISSUES_COLUMN_WIDTHS.PAGE_URL
		},
		headerStyle: {
			width: UNIQUE_ISSUES_COLUMN_WIDTHS.PAGE_URL,
			maxWidth: UNIQUE_ISSUES_COLUMN_WIDTHS.PAGE_URL
		},
		formatter: (url: string) => (
			<a href={url} target="_blank" rel="noopener noreferrer">
				{url}
			</a>
		)
	},
	{
		dataField: 'firstSeen',
		text: 'First seen',
		sort: true,
		style: { width: '180px' },
		formatter: (date: string) => formatShortMonthDatetime(date)
	},
	{
		dataField: 'lastSeen',
		text: 'Last seen',
		sort: true,
		style: { width: '180px' },
		formatter: (date: string) => formatShortMonthDatetime(date)
	},
	{
		dataField: 'actions',
		text: '',
		style: { width: UNIQUE_ISSUES_COLUMN_WIDTHS.ACTIONS },
		formatter: (_cell: unknown, row: IUniqueIssue) => (
			<ShowIssueDetailsCellFormatter
				infoTitle="Issue Details"
				issueType={row.issueType}
				renderDetailsBlock={(setError) => (
					<UniqueIssuesDetailsBlock
						runId={row.runId}
						issueId={row.issueId}
						productName={row.productName}
						setError={setError}
					/>
				)}
			/>
		)
	}
];
